import type { RecruitmentDeliveryState } from '@rudi/ui/recruitment-workspace';
import { RecruitmentApproval } from '@rudi/ui/recruitment-approval';
import { useMemo } from 'react';
import { useApiClient } from '@rudi/api/react';
import { RecruitmentWorkspace } from '@rudi/ui/recruitment-workspace';
import type {
  RecruitmentCampaign,
  RecruitmentEntry,
  RecruitmentOptions,
  RecruitmentTransport,
} from '@rudi/ui/recruitment-workspace';

const panelModules = import.meta.glob<{ register: () => void }>(
  '../../../../packages/ui/src/components/recruitment-panels/*.ts'
);
const teamPanelModules = Object.fromEntries(
  Object.entries(panelModules).filter(([path]) => /\/(copy|assets)\.ts$/.test(path))
);
export function RecruitmentCampaignsPanel({ teamBase }: { teamBase?: string } = {}) {
  const api = useApiClient();
  const transport = useMemo<RecruitmentTransport>(() => {
    const base = teamBase ? `${teamBase}/marketing` : '/cortex/clinical-trials/marketing';
    const assetsPath = (id: string) => `${base}/campaigns/${id}/assets`;
    const entries = (id: string) => `${base}/campaigns/${id}/entry-points`;
    return {
      customerActions: !teamBase,
      canAuthorWording: campaign => !teamBase || campaign.actions.includes('save'),
      panelIds: teamBase ? ['wording', 'assets'] : undefined,
      request: async <T,>(
        method: 'GET' | 'POST' | 'PUT',
        path: string,
        body?: unknown
      ): Promise<T> => {
        if (method === 'GET') return (await api.get<T>(`${base}${path}`)).data;
        if (method === 'PUT') return (await api.put<T>(`${base}${path}`, body)).data;
        return (await api.post<T>(`${base}${path}`, body)).data;
      },
      assets: {
        get: async id => (await api.get(assetsPath(id))).data,
        posters: async id => (await api.get(`${assetsPath(id)}/posters`)).data,
        acceptWording: async (id, expectedVersion) => {
          if (teamBase) throw new Error('Customer acceptance must be completed by the customer.');
          return (await api.post(`${assetsPath(id)}/accept-wording`, { expectedVersion })).data;
        },
        reusePoster: async (id, expectedVersion, posterId) =>
          (await api.post(`${assetsPath(id)}/reuse-poster`, { expectedVersion, posterId })).data,
        generate: async (id, expectedVersion) =>
          (await api.post(`${assetsPath(id)}/generate`, { expectedVersion })).data,
        select: async (id, expectedVersion, assetId) =>
          (await api.post(`${assetsPath(id)}/select`, { expectedVersion, assetId })).data,
        content: async (id, assetId) =>
          (await api.get(`${assetsPath(id)}/${assetId}/content`, { responseType: 'blob' })).data,
      },
      entryPoints: teamBase
        ? undefined
        : {
            list: async id => (await api.get<RecruitmentEntry[]>(entries(id))).data,
            save: async (id, entryId, input) =>
              entryId
                ? (await api.put<RecruitmentEntry>(`${entries(id)}/${entryId}`, input)).data
                : (await api.post<RecruitmentEntry>(entries(id), input)).data,
            verify: async (id, entryId, expectedVersion) =>
              (
                await api.post<RecruitmentEntry>(`${entries(id)}/${entryId}/verify`, {
                  expectedVersion,
                })
              ).data,
            remove: async (id, entryId, expectedVersion) => {
              await api.delete(`${entries(id)}/${entryId}`, { data: { expectedVersion } });
            },
          },
      delivery: teamBase
        ? undefined
        : {
            operator: true,
            inspect: async id =>
              (await api.get<RecruitmentDeliveryState>(`${base}/campaigns/${id}/delivery`)).data,
            command: async (id, action, revision) =>
              (
                await api.post<RecruitmentDeliveryState>(
                  `${base}/campaigns/${id}/delivery/${action}`,
                  revision ?? {}
                )
              ).data,
          },
      funding: teamBase
        ? undefined
        : {
            operator: true,
            request: async <T,>(
              path: string,
              method: 'get' | 'post' | 'put' = 'get',
              payload?: unknown
            ) => (await api[method]<T>(`${base}${path}`, payload)).data,
          },
      options: async () => (await api.get<RecruitmentOptions>(`${base}/options`)).data,
      list: async () => (await api.get<RecruitmentCampaign[]>(`${base}/campaigns`)).data,
      get: async id => (await api.get<RecruitmentCampaign>(`${base}/campaigns/${id}`)).data,
      create: async draft => (await api.post<RecruitmentCampaign>(`${base}/campaigns`, draft)).data,
      save: async (id, draft, expectedVersion) =>
        (
          await api.put<RecruitmentCampaign>(`${base}/campaigns/${id}`, {
            ...draft,
            expectedVersion,
          })
        ).data,
    };
  }, [api, teamBase]);
  return (
    <>
      {!teamBase && <RecruitmentApproval api={api} operator />}
      <RecruitmentWorkspace
        transport={transport}
        panelModules={teamBase ? teamPanelModules : panelModules}
      />
    </>
  );
}
